import React, { useState } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { useTheme } from '@mui/styles'
import type { NodeViewProps } from '@tiptap/react'
import type ThemeInterface from '@/theme-interface'

export const PlaceholderNodeView: React.FC<NodeViewProps> = ({ node, getPos, editor }) => {
  const { name, html } = node.attrs
  const theme = useTheme<ThemeInterface>()
  const options = editor.extensionManager.extensions.find(ext => ext.name === 'placeholder')?.options
  const brackets = options?.brackets || { open: '{{', close: '}}' }
  const [isDragging, setIsDragging] = useState(false)

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (typeof getPos === 'function') {
      const pos = getPos()
      editor.commands.setNodeSelection(pos)
    }
  }

  const handleDragStart = (event: React.DragEvent) => {
    setIsDragging(true)

    event.stopPropagation()

    if (typeof getPos === 'function') {
      const pos = getPos()
      const dragData = {
        type: 'placeholder-move',
        pos,
        name,
        html,
      }

      const dragDataString = JSON.stringify(dragData)
      event.dataTransfer.setData('text/plain', dragDataString)
      event.dataTransfer.setData('application/x-placeholder', dragDataString)
      event.dataTransfer.effectAllowed = 'move'

      editor.commands.setNodeSelection(pos)
    }
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  const commonProps = {
    draggable: true,
    onClick: handleClick,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
  }

  // Unified styles for both placeholder types (selection styles are handled by CSS)
  const placeholderStyles = {
    display: 'inline',
    cursor: isDragging ? 'grabbing' : 'grab',
    padding: '2px 4px',
    borderRadius: theme.borderRadius,
    border: `1px dashed ${theme.primary}`,
    backgroundColor: `${theme.primary}15`, // 15% opacity
    color: theme.primary,
    fontWeight: 'normal' as const,
    transition: 'all 0.2s ease-in-out',
    opacity: isDragging ? 0.5 : 1,
    userSelect: 'none' as const,
    position: 'relative' as const,
  }

  if (html) {
    // HTML placeholder with prevented default actions
    // Separate the HTML content into an inner span to prevent event interference
    return (
      <NodeViewWrapper>
        <span
          {...commonProps}
          className="placeholder-html"
          data-placeholder-html="true"
          data-placeholder-name={name}
          data-placeholder-content={html}
          contentEditable={false}
          style={placeholderStyles}
        >
          <span
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
              display: 'inline',
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </span>
      </NodeViewWrapper>
    )
  }

  // Text placeholder
  return (
    <NodeViewWrapper>
      <span
        {...commonProps}
        className="placeholder"
        data-placeholder-name={name}
        contentEditable={false}
        style={placeholderStyles}
      >
        {`${brackets.open}${name}${brackets.close}`}
      </span>
    </NodeViewWrapper>
  )
}
